import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useViewMode } from '../contexts/ViewModeContext';

const ADVANCED_IMPORT_TYPES = [
  'pricing-agreements',
  'pricing-agreement-customer-sites',
  'pricing-agreement-hire-rates',
  'pricing-agreement-sales-prices',
  'pricing-agreement-transport-rates',
  'sales-items-individual',
  'customer-equipment',
  'fleet-service-history',
  'meter-readings',
  'live-contracts',
];

const BASIC_EXPORT_TYPES = [
  'customers',
  'customer-contacts',
  'depots',
  'staff',
  'suppliers',
  'nominal-codes',
  'sales-categories',
  'customer-sites',
  'hire-categories',
  'hire-stock',
  'hire-stock-bulk',
  'sales-items',
  'hire-rates',
  'hire-category-groups',
  'fleet-reminders',
  'inspection-tests',
  'inclusions',
];

function csvHasDataRows(csvText) {
  const lines = csvText
    .trim()
    .split(/\r?\n/)
    .filter((line) => line.length > 0);
  return lines.length > 1;
}

function downloadCsvFile(filename, csvText) {
  const url = window.URL.createObjectURL(new Blob([csvText], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function ImportExport() {
  const { isAdvanced } = useViewMode();
  const [activeTab, setActiveTab] = useState('import');
  const [importType, setImportType] = useState('customers');
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    if (!isAdvanced && ADVANCED_IMPORT_TYPES.includes(importType)) {
      setImportType('customers');
    }
  }, [isAdvanced, importType]);

  const fetchLogs = async () => {
    try {
      const response = await axios.get('/api/csv/logs');
      setLogs(response.data.logs || response.data || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError('');
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const pollImportStatus = async (logId) => {
    while (true) {
      const response = await axios.get(`/api/csv/import-status/${logId}`);
      const data = response.data;
      setImportProgress((prev) => ({
        ...prev,
        phase: 'processing',
        status: data.status,
        processed: data.records_processed || 0,
        total: data.estimated_total_rows || 0,
        success: data.records_successful || 0,
        failed: data.records_failed || 0,
        percent: data.progress_percent || 0,
      }));

      if (data.status === 'completed' || data.status === 'failed') {
        return data;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file to import');
      return;
    }

    setImporting(true);
    setError('');
    setSuccess('');
    setImportResult(null);
    setImportProgress({
      phase: 'upload',
      uploadPercent: 0,
      processed: 0,
      total: 0,
      success: 0,
      failed: 0,
      percent: 0,
      status: 'uploading',
    });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`/api/csv/import/${importType}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (event) => {
          if (!event.total) {
            return;
          }
          const uploadPercent = Math.round((event.loaded / event.total) * 100);
          setImportProgress((prev) => ({
            ...prev,
            phase: 'upload',
            uploadPercent,
          }));
        },
      });

      const logId = response.data.log_id;
      if (!logId) {
        throw new Error('Import did not return a log id');
      }

      const finalStatus = await pollImportStatus(logId);
      const result = {
        rows_processed: finalStatus.records_processed || 0,
        success_count: finalStatus.records_successful || 0,
        failure_count: finalStatus.records_failed || 0,
        log_id: logId,
        status: finalStatus.status,
      };
      setImportResult(result);

      if (finalStatus.status === 'failed') {
        setError(finalStatus.error_details || 'Import failed');
      } else if (result.failure_count > 0) {
        setError(
          `Import completed with errors: ${result.success_count} successful, ${result.failure_count} failed. Check the history for details.`
        );
      } else {
        setSuccess(`Import completed: ${result.success_count} records imported successfully`);
      }
      setFile(null);
      fetchLogs();
    } catch (error) {
      setError(error.response?.data?.error || error.message || 'Import failed');
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  };

  const handleExport = async (type) => {
    setExporting(true);
    setError('');

    try {
      const response = await axios.get(`/api/csv/export/${type}`, {
        responseType: 'blob',
      });

      const csvText = await response.data.text();
      downloadCsvFile(`${type}_export_${Date.now()}.csv`, csvText);

      setSuccess(`${type} data exported successfully`);
      fetchLogs();
    } catch (error) {
      setError(error.response?.data?.error || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAll = async () => {
    const types = [...BASIC_EXPORT_TYPES, ...(isAdvanced ? ADVANCED_IMPORT_TYPES : [])];
    setExportingAll(true);
    setError('');
    setSuccess('');

    try {
      let exported = 0;
      for (const type of types) {
        const response = await axios.get(`/api/csv/export/${type}`, {
          responseType: 'blob',
        });
        const csvText = await response.data.text();
        if (!csvHasDataRows(csvText)) {
          continue;
        }

        downloadCsvFile(`${type}_export_${Date.now()}.csv`, csvText);
        exported += 1;
      }

      if (exported === 0) {
        setError('No tables with data to export.');
      } else {
        setSuccess(
          exported === 1 ? 'Exported 1 table with data.' : `Exported ${exported} tables with data.`
        );
        fetchLogs();
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Export all failed');
    } finally {
      setExportingAll(false);
    }
  };

  return (
    <div className="container-fluid p-5">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="h3 mb-0">Import/Export Data</h1>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          )}

          {success && (
            <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          )}

          <div
            className="alert alert-info d-flex align-items-center justify-content-between mb-4"
            role="alert"
          >
            <div className="d-flex align-items-center">
              <i className="bi bi-file-earmark-pdf-fill me-3" style={{ fontSize: '2rem' }}></i>
              <div>
                <h5 className="mb-1">📖 CSV Import Format Guide</h5>
                <p className="mb-0">
                  Download our comprehensive guide showing the required format for each CSV import
                  type.
                </p>
              </div>
            </div>
            <a
              href="/CSV_Import_Guide.html"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              <i className="bi bi-download me-2"></i>
              Download Guide
            </a>
          </div>

          <div className="card shadow-sm">
            <div className="card-header p-0">
              <ul className="nav nav-tabs card-header-tabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'import' ? 'active' : ''}`}
                    onClick={() => setActiveTab('import')}
                    type="button"
                    role="tab"
                  >
                    <i className="bi bi-upload me-2"></i>
                    Import Data
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${activeTab === 'export' ? 'active' : ''}`}
                    onClick={() => setActiveTab('export')}
                    type="button"
                    role="tab"
                  >
                    <i className="bi bi-download me-2"></i>
                    Export Data
                  </button>
                </li>
              </ul>
            </div>

            {activeTab === 'import' && (
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-12">
                    <h4 className="card-title mb-3">Import CSV Data</h4>
                    <p className="text-muted mb-4">
                      Upload a CSV file to import data into your database. Make sure the CSV format
                      matches the expected structure.
                    </p>

                    <form onSubmit={handleImport}>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Data Type</label>
                          <select
                            className="form-select"
                            value={importType}
                            onChange={(e) => setImportType(e.target.value)}
                          >
                            <option value="customers">Customers</option>
                            <option value="customer-sites">Customer Sites</option>
                            <option value="customer-contacts">Customer Contacts</option>
                            <option value="depots">Depots</option>
                            <option value="staff">Staff Members</option>
                            <option value="suppliers">Suppliers</option>
                            <option value="nominal-codes">Nominal Codes</option>
                            <option value="sales-categories">Sales Categories</option>
                            <option value="sales-items">Sales Items</option>
                            <option value="hire-categories">Hire Categories</option>
                            <option value="hire-category-groups">Hire Category Groups</option>
                            <option value="hire-rates">Hire Rates</option>
                            <option value="hire-stock">Hire Stock</option>
                            <option value="hire-stock-bulk">Hire Stock Bulk</option>
                            <option value="fleet-reminders">Fleet Reminders</option>
                            <option value="inspection-tests">Inspection Tests</option>
                            <option value="inclusions">Inclusions</option>
                            {isAdvanced && (
                              <>
                                <option value="pricing-agreements">Pricing Agreements</option>
                                <option value="pricing-agreement-customer-sites">
                                  Pricing Agreement Customer / Sites
                                </option>
                                <option value="pricing-agreement-hire-rates">
                                  Pricing Agreement Hire Rates
                                </option>
                                <option value="pricing-agreement-sales-prices">
                                  Pricing Agreement Sales Prices
                                </option>
                                <option value="pricing-agreement-transport-rates">
                                  Pricing Agreement Transport Rates
                                </option>
                                <option value="sales-items-individual">
                                  Sales Items Individual
                                </option>
                                <option value="customer-equipment">Customer Equipment</option>
                                <option value="fleet-service-history">Fleet Service History</option>
                                <option value="meter-readings">Meter Readings</option>
                                <option value="live-contracts">Live Contracts</option>
                              </>
                            )}
                          </select>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">CSV File</label>
                          <input
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            className="form-control"
                          />
                          {file && (
                            <div className="form-text text-success mt-2">
                              <i className="bi bi-check-circle me-1"></i>
                              Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="d-flex gap-2 mb-4">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={importing || !file}
                        >
                          {importing ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Importing...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-upload me-2"></i>
                              Import Data
                            </>
                          )}
                        </button>
                      </div>

                      {importProgress && (
                        <div className="card bg-light mb-4">
                          <div className="card-body">
                            <h6 className="mb-3">
                              {importProgress.phase === 'upload'
                                ? 'Uploading file...'
                                : importProgress.status === 'retrying'
                                  ? 'Retrying corrected records...'
                                  : 'Processing import...'}
                            </h6>
                            {importProgress.phase === 'upload' ? (
                              <>
                                <div className="progress mb-2">
                                  <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{ width: `${importProgress.uploadPercent}%` }}
                                    aria-valuenow={importProgress.uploadPercent}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  >
                                    {importProgress.uploadPercent}%
                                  </div>
                                </div>
                                <small className="text-muted">Uploading CSV to server</small>
                              </>
                            ) : (
                              <>
                                <div className="progress mb-2">
                                  <div
                                    className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                                    role="progressbar"
                                    style={{
                                      width: `${
                                        importProgress.total
                                          ? importProgress.percent
                                          : importProgress.processed > 0
                                            ? 100
                                            : 5
                                      }%`,
                                    }}
                                  >
                                    {importProgress.total
                                      ? `${importProgress.percent}%`
                                      : `${importProgress.processed} rows`}
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between small text-muted">
                                  <span>
                                    {importProgress.processed.toLocaleString()}
                                    {importProgress.total
                                      ? ` / ${importProgress.total.toLocaleString()} rows`
                                      : ' rows processed'}
                                  </span>
                                  <span>
                                    {importProgress.success.toLocaleString()} ok,{' '}
                                    {importProgress.failed.toLocaleString()} failed
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </form>

                    {importResult && (
                      <div className="card bg-light mt-4">
                        <div className="card-header">
                          <h5 className="card-title mb-0">
                            <i className="bi bi-bar-chart me-2"></i>
                            Import Results
                          </h5>
                        </div>
                        <div className="card-body p-4">
                          <div className="row">
                            <div className="col-md-4 mb-3">
                              <div className="text-center">
                                <div className="h4 text-primary mb-1">
                                  {importResult.rows_processed}
                                </div>
                                <small className="text-muted">Total Processed</small>
                              </div>
                            </div>
                            <div className="col-md-4 mb-3">
                              <div className="text-center">
                                <div className="h4 text-success mb-1">
                                  {importResult.success_count}
                                </div>
                                <small className="text-muted">Successful</small>
                              </div>
                            </div>
                            <div className="col-md-4 mb-3">
                              <div className="text-center">
                                <div className="h4 text-danger mb-1">
                                  {importResult.failure_count}
                                </div>
                                <small className="text-muted">Failed</small>
                              </div>
                            </div>
                          </div>

                          {importResult.errors && importResult.errors.length > 0 && (
                            <div className="mt-3">
                              <h6 className="text-danger">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                Errors:
                              </h6>
                              <div className="list-group list-group-flush">
                                {importResult.errors.slice(0, 10).map((error, index) => (
                                  <div
                                    key={index}
                                    className="list-group-item list-group-item-danger"
                                  >
                                    <strong>Row {error.row}:</strong> {error.error}
                                  </div>
                                ))}
                                {importResult.errors.length > 10 && (
                                  <div className="list-group-item list-group-item-warning">
                                    <i className="bi bi-three-dots me-2"></i>
                                    ... and {importResult.errors.length - 10} more errors
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'export' && (
              <div className="card-body p-4">
                <div className="row">
                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <h4 className="card-title mb-0">Export Data to CSV</h4>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleExportAll}
                        disabled={exporting || exportingAll}
                      >
                        {exportingAll ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            ></span>
                            Exporting all...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-download me-2"></i>
                            Export All
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-muted mb-4">
                      Download your data as CSV files for backup or external use.
                    </p>

                    <div className="row g-3">
                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-people me-2"></i>
                              Customers
                            </h5>
                            <p className="card-text text-muted">Export all customer records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('customers')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-person-lines-fill me-2"></i>
                              Customer Contacts
                            </h5>
                            <p className="card-text text-muted">
                              Export all customer contact records
                            </p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('customer-contacts')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-building me-2"></i>
                              Depots
                            </h5>
                            <p className="card-text text-muted">Export all depot records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('depots')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-person-badge me-2"></i>
                              Staff
                            </h5>
                            <p className="card-text text-muted">Export all staff member records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('staff')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-truck me-2"></i>
                              Suppliers
                            </h5>
                            <p className="card-text text-muted">Export all supplier records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('suppliers')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-calculator me-2"></i>
                              Nominal Codes
                            </h5>
                            <p className="card-text text-muted">Export all nominal code records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('nominal-codes')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-tags me-2"></i>
                              Sales Categories
                            </h5>
                            <p className="card-text text-muted">
                              Export all sales category records
                            </p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('sales-categories')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-geo-alt me-2"></i>
                              Customer Sites
                            </h5>
                            <p className="card-text text-muted">Export all customer site records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('customer-sites')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-gear me-2"></i>
                              Hire Categories
                            </h5>
                            <p className="card-text text-muted">Export all hire category records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('hire-categories')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-tools me-2"></i>
                              Hire Stock
                            </h5>
                            <p className="card-text text-muted">
                              Export all hire stock individual records
                            </p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('hire-stock')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-boxes me-2"></i>
                              Hire Stock Bulk
                            </h5>
                            <p className="card-text text-muted">
                              Export all hire stock bulk records
                            </p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('hire-stock-bulk')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-cart me-2"></i>
                              Sales Items
                            </h5>
                            <p className="card-text text-muted">Export all sales item records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('sales-items')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-currency-pound me-2"></i>
                              Hire Rates
                            </h5>
                            <p className="card-text text-muted">Export all hire rate records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('hire-rates')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-collection me-2"></i>
                              Hire Category Groups
                            </h5>
                            <p className="card-text text-muted">
                              Export all hire category group records
                            </p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('hire-category-groups')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-car-front me-2"></i>
                              Fleet Reminders
                            </h5>
                            <p className="card-text text-muted">
                              Export all fleet reminder records
                            </p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('fleet-reminders')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-clipboard-check me-2"></i>
                              Inspection Tests
                            </h5>
                            <p className="card-text text-muted">
                              Export all inspection test records
                            </p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('inspection-tests')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6 col-lg-4 mb-3">
                        <div className="card h-100 text-center">
                          <div className="card-body p-4">
                            <h5 className="card-title">
                              <i className="bi bi-list-check me-2"></i>
                              Inclusions
                            </h5>
                            <p className="card-text text-muted">Export all inclusion records</p>
                            <button
                              className="btn btn-success"
                              onClick={() => handleExport('inclusions')}
                              disabled={exporting || exportingAll}
                            >
                              {exporting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-download me-2"></i>
                                  Export
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {isAdvanced && (
                        <div className="row mt-2">
                          <div className="col-12">
                            <h6 className="text-muted mb-3">Pricing Agreements (Advanced)</h6>
                          </div>
                          {[
                            ['pricing-agreements', 'Pricing Agreements'],
                            ['pricing-agreement-customer-sites', 'Customer / Sites'],
                            ['pricing-agreement-hire-rates', 'Hire Rates'],
                            ['pricing-agreement-sales-prices', 'Sales Prices'],
                            ['pricing-agreement-transport-rates', 'Transport Rates'],
                          ].map(([slug, title]) => (
                            <div className="col-md-6 col-lg-4 mb-3" key={slug}>
                              <div className="card h-100 text-center">
                                <div className="card-body p-4">
                                  <h5 className="card-title">{title}</h5>
                                  <p className="card-text text-muted">
                                    Export {title.toLowerCase()}
                                  </p>
                                  <button
                                    className="btn btn-success"
                                    onClick={() => handleExport(slug)}
                                    disabled={exporting || exportingAll}
                                  >
                                    Export
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {isAdvanced && (
                        <div className="row mt-2">
                          <div className="col-12">
                            <h6 className="text-muted mb-3">Readings & Asset (Advanced)</h6>
                          </div>
                          {[
                            ['sales-items-individual', 'Sales Items Individual'],
                            ['customer-equipment', 'Customer Equipment'],
                            ['fleet-service-history', 'Fleet Service History'],
                            ['meter-readings', 'Meter Readings'],
                            ['live-contracts', 'Live Contracts'],
                          ].map(([slug, title]) => (
                            <div className="col-md-6 col-lg-4 mb-3" key={slug}>
                              <div className="card h-100 text-center">
                                <div className="card-body p-4">
                                  <h5 className="card-title">{title}</h5>
                                  <p className="card-text text-muted">
                                    Export {title.toLowerCase()}
                                  </p>
                                  <button
                                    className="btn btn-success"
                                    onClick={() => handleExport(slug)}
                                    disabled={exporting || exportingAll}
                                  >
                                    Export
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-4">
        <div className="card-header p-4">
          <h4 className="card-title mb-0">
            <i className="bi bi-clock-history me-2"></i>
            Import/Export History
          </h4>
        </div>
        <div className="card-body p-0">
          {logs.length === 0 ? (
            <div className="empty-state py-5">
              <i className="bi bi-clock-history display-4 text-muted mb-3"></i>
              <p className="text-muted mb-0">No import/export activity yet</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th className="border-0">Operation</th>
                    <th className="border-0">Data Type</th>
                    <th className="border-0">Filename</th>
                    <th className="border-0">Records</th>
                    <th className="border-0">Success</th>
                    <th className="border-0">Failed</th>
                    <th className="border-0">Date</th>
                    <th className="border-0">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>
                        <span
                          className={`badge ${log.operation_type.startsWith('import') ? 'bg-primary' : 'bg-success'}`}
                        >
                          <i
                            className={`bi ${log.operation_type.startsWith('import') ? 'bi-upload' : 'bi-download'} me-1`}
                          ></i>
                          {log.operation_type.replace('import_', '')}
                        </span>
                      </td>
                      <td className="name">{log.table_name}</td>
                      <td>
                        <code className="text-muted">{log.filename || '-'}</code>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark">{log.records_processed}</span>
                      </td>
                      <td>
                        <span className="text-success fw-bold">{log.records_successful}</span>
                      </td>
                      <td>
                        <span
                          className={`fw-bold ${log.records_failed > 0 ? 'text-danger' : 'text-success'}`}
                        >
                          {log.records_failed}
                        </span>
                      </td>
                      <td>
                        <small className="text-muted">
                          {new Date(log.created_at).toLocaleString()}
                        </small>
                      </td>
                      <td>
                        {log.records_failed > 0 && log.operation_type.startsWith('import') && (
                          <a
                            href={`/import-failures/${log.id}`}
                            className="btn btn-outline-danger btn-sm"
                            title="View and correct failed records"
                          >
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            View Failures
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportExport;
